const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Builds a Consumer path segment from an authoritative public_slug.
 * Clients must never invent SEO routes from the event name or fall back to UUID.
 * Returns empty string when public_slug is missing (callers must not link).
 */
export const createEventSlug = (event: {
  id: string;
  name?: string;
  public_slug?: string | null;
}): string => {
  return event.public_slug?.trim() ?? "";
};

/**
 * Extracts a full Event UUID from a UUID-only path segment.
 * Returns null for short SEO suffixes — those require the route resolver API
 * or trusted pagegen bootstrap metadata.
 *
 * Note: UUID-only public URLs are not a supported product route under the
 * clean-reset architecture; this helper remains for rare persisted historical
 * keys and internal debugging, not for constructing public links.
 */
export const extractEventIdFromSlug = (slug: string): string | null => {
  const trimmed = slug.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (uuidPattern.test(trimmed)) {
    return trimmed;
  }
  return null;
};

/**
 * True when the path segment is a full UUID.
 */
export const isUuidSlug = (slug: string): boolean => uuidPattern.test(slug.trim());

/**
 * Builds a corrected event path that preserves the current query string.
 */
export const buildCanonicalEventPath = (publicSlug: string, search?: string): string => {
  const slug = publicSlug.trim().replace(/^\/+|\/+$/g, "");
  const base = `/events/${slug}`;
  if (!search || search === "?") {
    return base;
  }
  return search.startsWith("?") ? `${base}${search}` : `${base}?${search}`;
};

export const formatSlugToTitle = (slug: string): string => {
  const trimmed = slug.trim();
  if (trimmed.length === 0) {
    return "Event";
  }
  const withoutShortSuffix = trimmed.replace(/-[0-9a-f]{8,64}$/i, "");
  const words = withoutShortSuffix
    .split(/[-_]+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
  if (words.length === 0) {
    return withoutShortSuffix.length > 0 ? withoutShortSuffix : "Event";
  }
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};
